import { Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Form, Stack } from '@carbon/react';
import { AccentField } from 'src/components/Storefront/AccentField';
import { ImageField } from 'src/components/Storefront/ImageField';
import { StorefrontThemeDto } from 'src/services/apis/types';

/**
 * `accentColor` is validated because it is edited again; `primaryColor` and `fontPair` are not,
 * because they are not.
 *
 * Those two stay in Formik's values and reach `onSave` through `onSubmit` untouched — Formik runs
 * the schema for its errors and submits its own `values`, so a key the schema does not declare is
 * carried through regardless. Validating them would be worse than pointless: a legacy value the
 * API no longer accepts would disable Save with no control left to fix it. The accent has a
 * control, so the opposite applies — a malformed hex must block Save rather than reach an API
 * that will reject it.
 */
const Schema = z.object({
  accentColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  logoFileId: z.string().nullish(),
  heroFileId: z.string().nullish(),
});

/**
 * The generated DTO types the image fields as optional `string`, but an unset image is
 * `null` everywhere it is held in the portal — that is what `ImageField` calls back with and
 * what the test supplies. The two are narrowed here rather than widened to `any`.
 */
export type StorefrontThemeValues = Omit<StorefrontThemeDto, 'logoFileId' | 'heroFileId'> & {
  logoFileId?: string | null;
  heroFileId?: string | null;
};

interface ThemeEditorProps {
  theme: StorefrontThemeValues;
  onSave: (theme: StorefrontThemeValues) => void;
  saving: boolean;
}

/**
 * Images and the accent. The palette and the typeface are the app's (spec §2, §4), so the
 * primary-colour field and the font-pairing select stay gone — their values are seeded from the
 * row and submitted unchanged, so the form preserves what it does not edit.
 *
 * The ACCENT is back, and the reason is worth recording: Plan 06 retired all three colour
 * controls while all three were inert, and the redesign then re-applied `accentColor` in
 * `themeStyle` without restoring its input. The result was a value the public site renders and
 * nobody can set — every storefront shipping the same `#c2410c` the server defaults to. A field
 * the site reads needs a field the owner can write.
 */
const ThemeEditor = React.memo(({ theme, onSave, saving }: ThemeEditorProps) => {
  const { t } = useTranslation();

  return (
    <Formik<StorefrontThemeValues>
      validationSchema={toFormikValidationSchema(Schema)}
      initialValues={{
        primaryColor: theme.primaryColor,
        accentColor: theme.accentColor,
        fontPair: theme.fontPair,
        logoFileId: theme.logoFileId ?? null,
        heroFileId: theme.heroFileId ?? null,
      }}
      onSubmit={(values) => onSave(values)}
    >
      {({ handleSubmit, values, setFieldValue, isValid }) => (
        <Form onSubmit={handleSubmit}>
          <Stack gap={6}>
            {/* `?? ''` because the generated DTO types every theme field optional; a storefront
                predating the accent default would otherwise put `undefined` into a controlled
                input and make React warn about switching to uncontrolled. */}
            <AccentField value={values.accentColor ?? ''} onChange={(next) => setFieldValue('accentColor', next)} />

            <ImageField
              title={t('storefront.appearance.logo')}
              description={t('storefront.appearance.logoHint')}
              aspectRatio={'1x1'}
              cropperAspectRatio={1}
              value={values.logoFileId ?? null}
              onChange={(fileId) => setFieldValue('logoFileId', fileId)}
            />

            <ImageField
              title={t('storefront.appearance.hero')}
              description={t('storefront.appearance.heroHint')}
              aspectRatio={'16x9'}
              cropperAspectRatio={16 / 9}
              value={values.heroFileId ?? null}
              onChange={(fileId) => setFieldValue('heroFileId', fileId)}
            />

            <Button type="submit" disabled={saving || !isValid}>
              {t('storefront.actions.save')}
            </Button>
          </Stack>
        </Form>
      )}
    </Formik>
  );
});

export { ThemeEditor };
export default ThemeEditor;
